import React, { useEffect, useState, useMemo } from "react";
import { 
  Box, Button, Typography, Paper, Divider, Stack, 
  IconButton, Tooltip, Skeleton, LinearProgress, 
  Card
} from "@mui/material";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
   LocationOn, Schedule, Close, 
  ZoomIn, ZoomOut,  Info,
} from "@mui/icons-material";
import { useShipmentFulfillmentDetailContext } from "contexts/ShipmentFulfillmentContext";
import { useLazyListCollectionStopsQuery } from "store/services/collectionApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";

// Create different icons for different stop types
const firstStopIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const lastStopIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const intermediateStopIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const fetchRouteWithOpenRouteService = async (start, end) => {
  const apiKey = "5b3ce3597851110001cf6248ee24c26fba2e47ea8efc9441625dc978";
  const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start.lon},${start.lat}&end=${end.lon},${end.lat}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    return data.features[0].geometry.coordinates.map((coord) => [
      coord[1],
      coord[0],
    ]); // Convert [lon, lat] to [lat, lon]
  } catch (error) {
    console.error("Error fetching route with OpenRouteService API:", error);
    return [
      [start.lat, start.lon],
      [end.lat, end.lon],
    ]; // Fallback to straight-line route
  }
};

const CollectionTrackTab = () => {
  const [selectedStop, setSelectedStop] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(9);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const { fulfillmentDetail } = useShipmentFulfillmentDetailContext();
  const collectionId = fulfillmentDetail?._id;

  const isLoaded = fulfillmentDetail?.shipmentId?.some(
    (shipment) => shipment.status === "Loaded"
  );

const validStatuses = [
  "Unloaded",
  "Deconsolidation",
  "CrossDocked",
  "SendOut",
  "Warehousing",
  "Delivered",
  "DeliveryFailed",
  "Return",
  "Cancelled",
];

const isDelivered = fulfillmentDetail?.shipmentId?.some(
  (shipment) => validStatuses.includes(shipment.status)
);


  const [fetchStops, { data: stopsRes, isError, isLoading }] = 
    useLazyListCollectionStopsQuery();

  const calculateCenter = useMemo(() => {
    if (!stopsRes?.stops?.length) return null;
    
    const validStops = stopsRes.stops.filter(
      stop => stop.stopLocation?.lat && stop.stopLocation?.lon
    );
    
    if (validStops.length === 0) return null;
    
    const sum = validStops.reduce((acc, stop) => {
      return {
        lat: acc.lat + stop.stopLocation.lat,
        lon: acc.lon + stop.stopLocation.lon
      };
    }, { lat: 0, lon: 0 });

    return {
      lat: sum.lat / validStops.length,
      lon: sum.lon / validStops.length
    };
  }, [stopsRes]);

  const MapController = () => {
    const map = useMap();
    
    useEffect(() => {
      if (calculateCenter) {
        map.flyTo([calculateCenter.lat, calculateCenter.lon], zoomLevel, {
          duration: 1
        });
      }
    }, [map]);
    
    return null;
  };

  useEffect(() => {
    if (collectionId && (isLoaded || isDelivered)) {
      fetchStops({ collectionId }).catch(() => {
        toast.error("Failed to fetch stops!");
      });
    }
  }, [collectionId, fetchStops, isLoaded, isDelivered]);

  useEffect(() => {
    if (calculateCenter) {
      setMapCenter([calculateCenter.lat, calculateCenter.lon]);
    } else {
      setMapCenter([27.6748, 85.4274]); 
    }
  }, [calculateCenter]);

  // Fetch routes when stops change
  useEffect(() => {
    const fetchRoutes = async () => {
      if (!stopsRes?.stops?.length) return;
      
      const validStops = stopsRes.stops.filter(
        stop => stop.stopLocation?.lat && stop.stopLocation?.lon
      );
      
      if (validStops.length < 2) return;
      
      const routes = [];
      
      for (let i = 0; i < validStops.length - 1; i++) {
        const start = {
          lat: validStops[i].stopLocation.lat,
          lon: validStops[i].stopLocation.lon
        };
        const end = {
          lat: validStops[i + 1].stopLocation.lat,
          lon: validStops[i + 1].stopLocation.lon
        };
        
        const route = await fetchRouteWithOpenRouteService(start, end);
        routes.push(...route);
      }
      
      setRouteCoordinates(routes);
    };
    
    fetchRoutes();
  }, [stopsRes]);

  if (isError) {
    toast.error("Failed to fetch stops!");
  }

  const handleMarkerClick = (stop) => {
    setSelectedStop(stop);
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 1, 18));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 1, 5));

  // Function to determine the appropriate icon for a stop
  const getStopIcon = (index, totalStops) => {
    if (index === 0) return lastStopIcon; 
    if (index === totalStops - 1) return firstStopIcon; 
    return intermediateStopIcon; 
  };

  return (
    <Box sx={{ 
      height: '100%', 
      maxHeight: 400,
      minHeight: 200,
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.default',
      p: 2
    }}>
      {isLoading ? (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      ) : (isLoaded||isDelivered) ? (
        <>
          <Box sx={{ 
            display: 'flex', 
            height: 'calc(100vh - 180px)',
            gap: 2,
            position: 'relative',
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 1,
            boxShadow: 1
          }}>
            {/* Map Container */}
            <Box sx={{ 
              flex: 1, 
              height: '100%',
              minHeight: 200,
              maxHeight: 400,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              {mapCenter ? (
                <MapContainer
                  center={mapCenter}
                  zoom={zoomLevel}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />

                  {/* Render the route as a blue line */}
                  {routeCoordinates.length > 0 && (
                    <Polyline
                      positions={routeCoordinates}
                      color="#1890ff"
                      weight={4}
                      opacity={0.8}
                    />
                  )}

                  {stopsRes?.stops?.map((stop, index, array) => (
                    stop.stopLocation?.lat && stop.stopLocation?.lon && (
                      <Marker
                        key={stop._id || index}
                        position={[stop.stopLocation.lat, stop.stopLocation.lon]}
                        icon={getStopIcon(index, array.length)}
                        eventHandlers={{
                          click: () => handleMarkerClick(stop),
                        }}
                      >
                        <Popup>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {stop.stopLocation?.location || 'Unnamed Location'}
                          </Typography>
                          <Typography variant="subtitle2" color="text.black">
                            <AppTableDateTime date={stop.stopTime} />
                          </Typography>
                        </Popup>
                      </Marker>
                    )
                  ))}
                  
                  <MapController />
                </MapContainer>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              )}

              {/* Custom Map Controls */}
              <Box sx={{
                position: 'absolute',
                right: 16,
                bottom: 16,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '& .MuiIconButton-root': {
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText'
                  }
                }
              }}>
                <Tooltip title="Zoom In" placement="left">
                  <IconButton onClick={handleZoomIn}>
                    <ZoomIn />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out" placement="left">
                  <IconButton onClick={handleZoomOut}>
                    <ZoomOut />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Stop Details Panel */}
            {selectedStop ? (
              <Box sx={{ 
                width: { xs: '100%', md: 350 },
                height: '100%',
                maxHeight: 400,
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease'
              }}>
                <Paper elevation={0} sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocationOn color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        {selectedStop.stopLocation?.location || 'Stop Details'}
                      </Typography>
                    </Stack>
                    <IconButton 
                      onClick={() => setSelectedStop(null)} 
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <Close />
                    </IconButton>
                  </Stack>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Card sx={{ flex: 1, overflow: 'auto', p: 2, borderRadius: 2, boxShadow: 2 }}>
                    <Stack spacing={3}>
                      {/* Stop Time */}
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Schedule color="primary" sx={{ fontSize: 22 }} />
                          <Typography variant="subtitle1" color="text.primary">
                            Stop Time
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={500} color="text.secondary" sx={{ pl: 2 }}>
                          <AppTableDateTime date={selectedStop.stopTime} />
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Schedule color="primary" sx={{ fontSize: 22 }} />
                          <Typography variant="subtitle1" color="text.primary">
                            Creation Time
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={500} color="text.secondary" sx={{ pl: 2 }}>
                          <AppTableDateTime date={selectedStop.createdAt} />
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Schedule color="primary" sx={{ fontSize: 22 }} />
                          <Typography variant="subtitle1" color="text.primary">
                            Updated Time
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={500} color="text.secondary" sx={{ pl: 2 }}>
                          <AppTableDateTime date={selectedStop.updatedAt} />
                        </Typography>
                      </Box>
                      <Divider />
                    </Stack>
                  </Card>
                </Paper>
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: { xs: '100%', md: 350 },
                height: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                p: 3,
                textAlign: 'center'
              }}>
                <LocationOn color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Stop Selected
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Click on any marker to view stop details
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<Info />}
                  onClick={() => setSelectedStop(stopsRes?.stops?.[0])}
                >
                  View First Stop
                </Button>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 4
        }}>
          <Paper elevation={0} sx={{ 
            p: 4, 
            textAlign: 'center',
            maxWidth: 500,
            borderRadius: 2,
            width: '100%'
          }}>
            <Box sx={{ 
              bgcolor: 'action.hover',
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <LocationOn color="disabled" sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Tracking Not Available
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Stops can only be tracked when the shipment is loaded or delivered.
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default CollectionTrackTab;